import React, { useState } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FormActionButtons from "../Assets/FormActionButtons";
import ImageUpload from "../Assets/ImageUpload";
import LanguageTabs, { FormLang } from "../Assets/LanguageTabs";
import { useTranslation } from "react-i18next";

interface ProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (product: FormData) => void; // Accept FormData
    category: string;
    name: string;
}

type Multi = { en: string; mk: string; sq: string };
const emptyMulti: Multi = { en: "", mk: "", sq: "" };

const ProductModal: React.FC<ProductModalProps> = ({ isOpen, onClose, onSubmit, name, category }) => {
    const { t } = useTranslation();

    const [activeLang, setActiveLang] = useState<FormLang>("en");
    const [productName, setProductName] = useState<Multi>({ ...emptyMulti });
    const [description, setDescription] = useState<Multi>({ ...emptyMulti });
    const [dosage, setDosage] = useState<Multi>({ ...emptyMulti });
    const [image, setImage] = useState<File | null>(null);

    const setField = (
        setter: React.Dispatch<React.SetStateAction<Multi>>,
        value: string
    ) => setter((prev) => ({ ...prev, [activeLang]: value }));

    const resetForm = () => {
        setProductName({ ...emptyMulti });
        setDescription({ ...emptyMulti });
        setDosage({ ...emptyMulti });
        setImage(null);
        setActiveLang("en");
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const formData = new FormData();
        if (image) {
            formData.append("image", image);
        }
        formData.append("name", JSON.stringify(productName));
        formData.append("description", JSON.stringify(description));
        formData.append("dosage", JSON.stringify(dosage));
        formData.append("categoryName", category);

        onSubmit(formData);
        resetForm();
        onClose();
    };

    const stripHtml = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent?.trim() || '';
    };

    const isQuillEmpty = (html: string) => stripHtml(html).length === 0;

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    // Required fields are the base language (EN) + MK; SQ is optional (falls back).
    const isDisabled =
        !productName.en || !productName.mk ||
        isQuillEmpty(description.en) || isQuillEmpty(description.mk) ||
        !dosage.en || !dosage.mk;

    if (!isOpen) return null;

    const langSuffix = activeLang.toUpperCase();

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-semibold mb-6 text-blue-900 text-center">
                    {t('forms.createProduct')} - {name}
                </h2>

                <LanguageTabs active={activeLang} onChange={setActiveLang} />

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block font-medium mb-1">{t('forms.name')} ({langSuffix})</label>
                        <input
                            type="text"
                            value={productName[activeLang]}
                            onChange={(e) => setField(setProductName, e.target.value)}
                            className="w-full border p-3 rounded"
                            required={activeLang !== "sq"}
                        />
                    </div>

                    <div>
                        <label className="block font-medium mb-1">{t('forms.description')} ({langSuffix})</label>
                        <ReactQuill
                            key={`desc-${activeLang}`}
                            value={description[activeLang]}
                            onChange={(value) => setField(setDescription, value)}
                            theme="snow"
                            className="h-64"
                        />
                    </div>
                    <br />
                    <br />

                    <div>
                        <label className="block font-medium mb-1">{t('forms.dosage')} ({langSuffix})</label>
                        <input
                            type="text"
                            value={dosage[activeLang]}
                            onChange={(e) => setField(setDosage, e.target.value)}
                            className="w-full border p-3 rounded"
                            required={activeLang !== "sq"}
                        />
                    </div>

                    <ImageUpload onImageSelect={(file) => setImage(file)} />

                    <FormActionButtons
                        edit={false}
                        onClose={handleCancel}
                        onSubmit={handleSubmit}
                        disabled={isDisabled}
                    />
                </form>
            </div>
        </div>
    );
};

export default ProductModal;
